import { isValidElement } from 'react';

import { css, styled } from '@mui/material';

import { Label } from '../Label';
import { KeyValuePairKeyWidthMap } from './constants';
import { keyValuePairItemClasses } from './theme';

import type { ReactElement } from 'react';
import type { LabelProps } from '../Label';
import type { KeyValuePairRowHeight } from './constants';
import type { KeyValuePairItemOwnerState, KeyValuePairItemProps } from './types';

// theme.ts에서 이동한 헬퍼 함수
function getLabelSize(
  label: KeyValuePairItemProps['label'],
  rowSize: KeyValuePairRowHeight = 'md',
): NonNullable<LabelProps['size']> {
  if (isValidElement(label)) {
    return ((label as ReactElement<LabelProps, typeof Label>)?.props.size ||
      rowSize) as NonNullable<LabelProps['size']>;
  }
  return rowSize;
}

export default function KeyValuePairItem(props: KeyValuePairItemProps) {
  /* shouldForwardProp 방지 : labelPosition */
  const {
    label: labelProp,
    value,
    rowSize = 'md',
    labelPosition = 'left', // 기본값 설정 (theme defaultProps에서 이동)
    align = 'baseline',
    ...others
  } = props;

  const ownerState: KeyValuePairItemOwnerState = {
    ...props,
    labelPosition,
    rowSize,
    align,
  };

  let label = labelProp;
  if (labelProp) {
    let passingProps: LabelProps = {
      label: '',
      size: rowSize,
    };
    if (typeof labelProp === 'string') {
      passingProps = { ...passingProps, label: labelProp };
    }
    if (isValidElement(labelProp) && labelProp.type === Label) {
      const element = labelProp as ReactElement<LabelProps, typeof Label>;
      passingProps = { ...passingProps, ...element.props };
    }
    label = <KeyValuePairItemLabel type="secondary" {...passingProps} ownerState={ownerState} />;
  }

  return (
    <KeyValuePairItemRoot ownerState={ownerState} {...others}>
      {label && (
        <KeyValuePairItemLabelSection ownerState={ownerState}>{label}</KeyValuePairItemLabelSection>
      )}
      <KeyValuePairItemValueSection ownerState={ownerState}>{value}</KeyValuePairItemValueSection>
    </KeyValuePairItemRoot>
  );
}

// Styled Components
const KeyValuePairItemRoot = styled('div', {
  target: keyValuePairItemClasses.root,
})<{ ownerState: KeyValuePairItemOwnerState }>(({ ownerState }) => {
  const { labelPosition, label, rowSize, showSideMargin, align = 'baseline' } = ownerState;
  const labelSize = getLabelSize(label, rowSize);

  return css`
    display: flex;
    align-items: ${align};
    gap: 16px;
    padding: ${showSideMargin ? '4px 16px' : 0};
    word-break: break-word;
    overflow-wrap: break-word;

    ${labelPosition === 'top' &&
    css`
      flex-direction: column;
      row-gap: 2px; /* Vertical 레이아웃일 경우 간격 부여 (Label - Value) */
    `}

    ${labelPosition === 'left' &&
    css`
      flex-direction: row;
      min-height: ${labelSize === 'sm' ? '28px' : '32px'};
    `}
  `;
});

const KeyValuePairItemLabelSection = styled('div', {
  target: keyValuePairItemClasses.labelSection,
})<{ ownerState: KeyValuePairItemOwnerState }>(({ ownerState }) => {
  const { labelPosition, keySize = 'md' } = ownerState;

  return css`
    display: flex;
    align-items: center;

    ${labelPosition === 'left' &&
    css`
      min-width: ${KeyValuePairKeyWidthMap[keySize]}px;
      max-width: ${KeyValuePairKeyWidthMap[keySize]}px;
    `}
  `;
});

const KeyValuePairItemLabel = styled(Label, {
  target: keyValuePairItemClasses.label,
})<{ ownerState: KeyValuePairItemOwnerState }>(({ size }) => {
  return css`
    padding: ${size === 'sm' ? '4px 0' : '5.5px 0'};
  `;
});

const KeyValuePairItemValueSection = styled('div', {
  target: keyValuePairItemClasses.valueSection,
})<{ ownerState: KeyValuePairItemOwnerState }>(({ ownerState, theme }) => {
  const { label, rowSize } = ownerState;
  const labelSize = getLabelSize(label, rowSize);

  return css`
    // NOTE: label size에 맞는 기본 typography variant 적용 : https://www.figma.com/design/ZeLzIUbJDNc6z8U8fPLFKW/%F0%9F%92%8E-KCDS-Components--product-?node-id=35618-86071&t=zLV8IV88UW294v54-0
    ${labelSize === 'sm' ? theme.typography['body/normal/sm'] : theme.typography['body/normal/md']}
    flex : 1 1 auto;
    min-width: 0px;
    padding: ${labelSize === 'sm' ? '4px 0' : '5.5px 0'};
  `;
});
